import { useCallback, useMemo } from 'react';

import {
  Checkbox,
  Divider,
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
  makeStyles,
} from '@material-ui/core';

import { FilterEditorComponentProps } from '@spotify/confidence-core-react';

import { WorkflowExtensionOptions } from '../../extensions';
import { useWorkflowExtensions } from '../../hooks';

const useStyles = makeStyles(theme => ({
  menuItem: {
    paddingRight: theme.spacing(2),
  },
  typeIcon: {
    display: 'inline-flex',
    verticalAlign: 'text-bottom',
    marginRight: theme.spacing(1),
    color: theme.palette.text.secondary,
    '& svg': {
      width: 16,
      height: 16,
    },
  },
}));

export const normalizeTypeId = (id: string) =>
  id === 'reanalysis' ? 'analysis' : id;

export const TypeFilterEditor = (
  props: FilterEditorComponentProps<string[]> & {
    excludeWorkflowIds?: string[];
  },
) => {
  const workflowExtensions = useWorkflowExtensions();
  const { value, onValueChange, onClose, excludeWorkflowIds } = props;
  const classes = useStyles();

  const visibleExtensions = useMemo(() => {
    let exts = workflowExtensions;
    if (excludeWorkflowIds?.length) {
      const excluded = new Set(excludeWorkflowIds);
      exts = exts.filter(w => !excluded.has(w.workflowId));
    }
    const ids = new Set(exts.map(w => w.workflowId));
    const deduped =
      ids.has('analysis') && ids.has('reanalysis')
        ? exts.filter(w => w.workflowId !== 'reanalysis')
        : exts;
    return [...deduped].sort((a, b) => a.title.localeCompare(b.title));
  }, [workflowExtensions, excludeWorkflowIds]);

  const typeIds = useMemo(
    () => visibleExtensions.map(w => normalizeTypeId(w.workflowId)),
    [visibleExtensions],
  );

  const handleItemClick = useCallback(
    (workflowId: string) => () => {
      const id = normalizeTypeId(workflowId);
      onValueChange(
        value?.includes(id)
          ? value.filter(v => v !== id)
          : [...(value ?? []), id],
      );
    },
    [value, onValueChange],
  );

  const handleCheckboxChange = useCallback(
    (workflowId: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const id = normalizeTypeId(workflowId);
      onValueChange(
        e.target.checked
          ? [...(value ?? []), id]
          : (value ?? []).filter(v => v !== id),
      );
    },
    [value, onValueChange],
  );

  return (
    <MenuList>
      <MenuItem
        button
        dense
        disableGutters
        className={classes.menuItem}
        onClick={() => {
          onValueChange(value?.length === typeIds.length ? [] : typeIds);
          onClose?.();
        }}
       data-track-source-anchor="988cdc8dce;type-filter-editor-menu-item;low">
        <ListItemIcon>
          <Checkbox
            edge="start"
            checked={value?.length === typeIds.length}
            onClick={e => e.stopPropagation()}
            onChange={e => onValueChange(e.target.checked ? typeIds : [])}
            indeterminate={
              (value?.length ?? 0) > 0 &&
              (value?.length ?? 0) < visibleExtensions.length
            }
           data-track-source-anchor="1210c15de2;type-filter-editor-checkbox;low"/>
        </ListItemIcon>
        <ListItemText primary="All" />
      </MenuItem>

      <Divider />

      {visibleExtensions.map((item: WorkflowExtensionOptions) => (
        <MenuItem
          key={item.workflowId}
          button
          dense
          onClick={handleItemClick(item.workflowId)}
          disableGutters
          className={classes.menuItem}
         data-track-source-anchor="62e4a39ab1;type-filter-editor-menu-item-2;low">
          <ListItemIcon>
            <Checkbox
              edge="start"
              checked={
                value?.includes(normalizeTypeId(item.workflowId)) ?? false
              }
              onClick={e => e.stopPropagation()}
              onChange={handleCheckboxChange(item.workflowId)}
              tabIndex={-1}
              disableRipple
             data-track-source-anchor="1b9b79281b;type-filter-editor-checkbox-2;low"/>
          </ListItemIcon>
          <ListItemText
            primary={
              <>
                {item.workflowIcon && (
                  <span className={classes.typeIcon}>
                    <item.workflowIcon />
                  </span>
                )}
                {item.title}
              </>
            }
          />
        </MenuItem>
      ))}
    </MenuList>
  );
};
